import * as React from 'react'
import { Navigate } from 'react-router-dom'
import { Misuse, Restart, Home } from '@carbon/icons-react'
import { Button, InlineNotification, Tile, Stack, Section } from '@carbon/react'

function is404Error(error: unknown): boolean {
  const err = error as { response?: { status?: number }; status?: number }
  return err?.response?.status === 404 || err?.status === 404
}

interface ErrorViewProps {
  title?: string
  message?: string
  error?: Error | string
  withOverlay?: boolean
  onRetry?: () => void
  onHome?: () => void
  /** Si fourni, redirige vers cette URL quand l'erreur est un 404 (ex: pas d'établissement) */
  redirectOn404?: string
  className?: string
  variant?: 'inline' | 'card' | 'full'
}

export function ErrorView({
  title = 'Une erreur est survenue',
  message,
  error,
  withOverlay = false,
  onRetry,
  onHome,
  redirectOn404,
  className = '',
  variant = 'full',
}: ErrorViewProps) {
  if (redirectOn404 && error && is404Error(error)) {
    return <Navigate to={redirectOn404} replace />
  }

  const errorMessage = error
    ? typeof error === 'string'
      ? error
      : error.message
    : message || "Une erreur inattendue s'est produite."

  // 1. Variante INLINE : Standard Carbon
  if (variant === 'inline') {
    return (
      <InlineNotification
        kind="error"
        title={title}
        subtitle={errorMessage}
        className={className}
        lowContrast
      />
    )
  }

  // 2. Variante CARD : Utilisation de Tile
  if (variant === 'card') {
    return (
      <Tile
        className={className}
        style={{ borderLeft: '4px solid var(--cds-support-error)' }}
      >
        <Stack gap={5}>
          <div
            style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}
          >
            <Misuse size={24} color="var(--cds-support-error)" />
            <div style={{ flex: 1 }}>
              <h3 style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
                {title}
              </h3>
              <p
                style={{
                  marginBottom: '1rem',
                  color: 'var(--cds-text-secondary)',
                }}
              >
                {errorMessage}
              </p>
              {onRetry && (
                <Button
                  onClick={onRetry}
                  kind="tertiary"
                  size="sm"
                  renderIcon={Restart}
                >
                  Réessayer
                </Button>
              )}
            </div>
          </div>
        </Stack>
      </Tile>
    )
  }

  // 3. Variante FULL : Centrage Carbon
  const overlayStyle: React.CSSProperties = withOverlay
    ? {
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--cds-background)',
        zIndex: 9000,
        padding: '2rem',
      }
    : {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4rem 2rem',
      }

  return (
    <div style={overlayStyle} className={className}>
      <div style={{ maxWidth: '400px', textAlign: 'center' }}>
        <Stack gap={7}>
          <div>
            <Misuse
              size={64}
              color="var(--cds-support-error)"
              style={{ marginBottom: '1.5rem' }}
            />
            <h2
              style={{
                fontSize: 'var(--cds-productive-heading-05)',
                marginBottom: '1rem',
              }}
            >
              {title}
            </h2>
            <p
              style={{
                color: 'var(--cds-text-secondary)',
                marginBottom: '2rem',
              }}
            >
              {errorMessage}
            </p>
          </div>

          <div
            style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}
          >
            {onRetry && (
              <Button onClick={onRetry} renderIcon={Restart}>
                Réessayer
              </Button>
            )}
            {onHome && (
              <Button onClick={onHome} kind="secondary" renderIcon={Home}>
                Retour à l'accueil
              </Button>
            )}
          </div>
        </Stack>
      </div>
    </div>
  )
}

/**
 * Composants spécialisés
 */

export function NetworkErrorView({ onRetry }: { onRetry?: () => void }) {
  return (
    <ErrorView
      title="Erreur de connexion"
      message="Impossible de se connecter au serveur. Vérifiez votre connexion internet."
      onRetry={onRetry}
    />
  )
}

export function NotFoundView({
  resourceName = 'ressource',
  onHome,
}: {
  resourceName?: string
  onHome?: () => void
}) {
  return (
    <ErrorView
      title="Introuvable"
      message={`La ${resourceName} recherchée n'existe pas.`}
      onHome={onHome}
    />
  )
}

export function ForbiddenView({ onHome }: { onHome?: () => void }) {
  return (
    <ErrorView
      title="Accès refusé"
      message="Vous n'avez pas les autorisations nécessaires."
      onHome={onHome}
    />
  )
}
